import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { PaymentRecord } from '@/types/domain/payment-management';
import { formatCurrency } from '@/lib/utils';
import {
  FileCheck,
  X,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';

interface PaymentReconcileModalProps {
  isOpen: boolean;
  payment: PaymentRecord;
  onClose: () => void;
  onReconcile: (resolutionNotes: string) => Promise<void>;
}

export const PaymentReconcileModal: React.FC<PaymentReconcileModalProps> = ({
  isOpen,
  payment,
  onClose,
  onReconcile,
}) => {
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setResolutionNotes('');
      setErrorMessage(null);
    } else {
      document.body.style.overflow = '';
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resolutionNotes.trim()) {
      setErrorMessage('Treasury reconciliation justification notes are mandatory.');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage(null);
      await onReconcile(resolutionNotes.trim());
      onClose();
    } catch (err: any) {
      setErrorMessage(err?.message || 'Failed to reconcile payment transaction.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="payment-reconcile-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-xs animate-in fade-in duration-200"
    >
      <div className="relative w-full max-w-lg bg-card border border-border rounded-2xl shadow-xl overflow-hidden font-heading flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-border flex items-center justify-between bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-amber-500/10 text-amber-600">
              <FileCheck className="h-5 w-5" />
            </div>
            <div>
              <h2 id="payment-reconcile-modal-title" className="text-base font-bold text-foreground">
                Reconcile Bank Transaction
              </h2>
              <p className="text-xs text-foreground-muted">
                Payment #{payment.paymentReference} — Invoice #{payment.invoiceNumber}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-foreground-muted hover:text-foreground rounded-lg transition-colors cursor-pointer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content & Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {errorMessage && (
            <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-xl flex items-start gap-2.5 text-xs text-rose-700 dark:text-rose-400">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Variance Comparison Box */}
          <div className="p-3.5 bg-muted/40 rounded-xl border border-border space-y-2 text-xs">
            <div className="flex justify-between font-medium">
              <span className="text-foreground-muted">Internal Disbursed Amount:</span>
              <span className="font-bold text-foreground">
                {formatCurrency(payment.amount, payment.currency)}
              </span>
            </div>
            {payment.reconciliationDetails && (
              <div className="flex justify-between font-medium">
                <span className="text-foreground-muted">Bank Statement Cleared:</span>
                <span className="font-bold text-amber-600">
                  {formatCurrency(payment.reconciliationDetails.externalAmount || 0, payment.currency)}
                </span>
              </div>
            )}
            <div className="flex justify-between font-medium pt-1 border-t border-border/60">
              <span className="text-foreground-muted">Beneficiary Vendor:</span>
              <span className="font-bold text-foreground">{payment.vendorName}</span>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-foreground-secondary mb-1.5 block">
              Treasury Reconciliation Notes & Justification <span className="text-rose-600">*</span>
            </label>
            <textarea
              rows={3}
              required
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              placeholder="e.g. Bank debit advice confirmed; partial fee deduction of ₹150 recognized as bank transaction charge..."
              className="w-full px-3.5 py-2.5 bg-background border border-border rounded-xl text-xs font-medium focus:ring-2 focus:ring-primary focus:outline-hidden resize-none"
            />
          </div>

          {/* Footer */}
          <div className="pt-3 border-t border-border flex items-center justify-end gap-3">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin mr-1.5" /> Reconciling...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-1.5" /> Confirm Reconciliation
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
